import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, ImagePlus, GripVertical, Video } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { validateVideoFile, generateVideoThumbnail, isVideoFile, formatFileSize } from '../utils/mediaUtils';
import { blogAPI } from '../api/blogApi';

export interface PostImage {
  id?: number; // ID từ backend sau khi upload
  url: string;
  type?: 'image' | 'video'; // Media type (from backend or set during upload)
  caption?: string;
  sort_order: number;
  driveFileId?: string; // Google Drive file ID để xóa sau này
  drive_file_id?: string | null;
  drive_owner_id?: number | null;
  isUploading?: boolean;
  thumbnailUrl?: string; // Thumbnail for videos
  file?: File;
}

interface ImageUploadSectionProps {
  images: PostImage[];
  onImagesChange: (images: PostImage[]) => void;
  maxImages?: number;
  maxVideos?: number;
  disabled?: boolean;
  /** Post ID for organized folder structure (post-{id}/) - optional for create, required for edit */
  postId?: number;
  deferUpload?: boolean;
  onEnsurePostId?: () => Promise<number | null>;
  blogId: number;
  coverImageId?: number | null;
  onCoverChange?: (coverImageId: number | null) => void;
}

export const ImageUploadSection = ({
  images,
  onImagesChange,
  maxImages = 50,
  maxVideos = 3,
  disabled = false,
  postId,
  blogId,
  deferUpload = false,
  onEnsurePostId,
  coverImageId,
  onCoverChange,
}: ImageUploadSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Count current videos and images
  const currentVideoCount = images.filter(img => img.type === 'video').length;
  const currentImageCount = images.filter(img => img.type !== 'video').length;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // Count videos and images in selected files
    let newVideoCount = 0;
    let newImageCount = 0;

    for (const file of files) {
      if (isVideoFile(file.name, file.type)) {
        newVideoCount++;
      } else {
        newImageCount++;
      }
    }

    // Check video limit
    if (currentVideoCount + newVideoCount > maxVideos) {
      toast.error(`Mỗi bài viết chỉ được tải tối đa ${maxVideos} video. Hiện tại: ${currentVideoCount}/${maxVideos}`);
      return;
    }

    // Check image limit
    if (currentImageCount + newImageCount > maxImages) {
      toast.error(`Mỗi bài viết chỉ được tải tối đa ${maxImages} ảnh. Hiện tại: ${currentImageCount}/${maxImages}`);
      return;
    }

    // Validate files (especially videos)
    const validFiles: File[] = [];
    for (const file of files) {
      const isVideo = isVideoFile(file.name, file.type);

      if (isVideo) {
        const validation = validateVideoFile(file);
        if (!validation.valid) {
          toast.error(validation.error);
          continue;
        }
        toast.info(`Đang upload video ${file.name} (${formatFileSize(file.size)})...`);
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    // Create temporary images/videos with loading state
    const tempImages: PostImage[] = await Promise.all(
      validFiles.map(async (file, index) => {
        const isVideo = isVideoFile(file.name, file.type);
        let thumbnailUrl: string | undefined;

        if (isVideo) {
          try {
            thumbnailUrl = await generateVideoThumbnail(file);
          } catch (error) {
            console.error('Failed to generate thumbnail:', error);
          }
        }

        return {
          url: URL.createObjectURL(file),
          type: isVideo ? 'video' : 'image',
          sort_order: images.length + index,
          isUploading: !deferUpload,
          file,
          thumbnailUrl,
        };
      })
    );

    onImagesChange([...images, ...tempImages]);

    if (deferUpload) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    let resolvedPostId = postId;
    if (!resolvedPostId && onEnsurePostId) {
      try {
        resolvedPostId = await onEnsurePostId() ?? undefined;
      } catch (error) {
        console.error('[ImageUpload] Could not create draft post:', error);
        onImagesChange([...images, ...tempImages.map((image) => ({ ...image, isUploading: false }))]);
        toast.error('Không thể tạo bản nháp để tải ảnh lên');
        return;
      }
    }

    // Upload through blog-worker; the worker uses the blog owner's Drive.
    const uploadedImages: PostImage[] = [];
    for (let i = 0; i < validFiles.length; i++) {
      try {
        const file = validFiles[i];
        const isVideo = isVideoFile(file.name, file.type);

        console.log(`[ImageUpload] Uploading ${isVideo ? 'video' : 'image'}: ${file.name}`);

        // Upload with postId if available (for organized folder structure)
        const result = await blogAPI.admin.uploadBlogMedia(blogId, file, resolvedPostId, 'post-media');

        console.log(`[ImageUpload] Upload success:`, {
          fileName: file.name,
          driveUrl: result.data.url,
          fileId: result.data.fileId
        });

        let persistedImage: PostImage = {
          url: result.data.url, // Owner's Google Drive URL
          type: (isVideo ? 'video' : 'image') as 'image' | 'video',
          driveFileId: result.data.fileId,
          drive_file_id: result.data.fileId,
          drive_owner_id: Number(result.data.ownerId),
          sort_order: images.length + i,
          isUploading: false,
          thumbnailUrl: tempImages[i].thumbnailUrl,
        };

        // Persist the association immediately. This keeps media attached to a
        // draft even when the editor is closed before the post is published.
        if (resolvedPostId) {
          try {
            const saved = await blogAPI.admin.addPostImage(resolvedPostId, {
              url: persistedImage.url,
              type: persistedImage.type,
              sort_order: persistedImage.sort_order,
              drive_file_id: persistedImage.drive_file_id ?? undefined,
              drive_owner_id: persistedImage.drive_owner_id ?? undefined,
            });
            persistedImage = {
              ...persistedImage,
              id: saved.data.id,
            };
            if (!coverImageId && images.length === 0 && i === 0 && persistedImage.type === 'image' && onCoverChange) {
              onCoverChange(persistedImage.id ?? null);
            }
          } catch (persistError) {
            // Avoid leaving an untracked file in the owner's Drive.
            await blogAPI.admin.deleteBlogMedia(blogId, result.data.fileId).catch(() => undefined);
            throw persistError;
          }
        }

        const uploadedImage = persistedImage;

        uploadedImages.push(uploadedImage);

        // Update UI with each successful upload
        const currentUploaded = [...images, ...uploadedImages];
        const remainingTemp = tempImages.slice(uploadedImages.length);

        console.log(`[ImageUpload] Updating state with ${currentUploaded.length} uploaded, ${remainingTemp.length} remaining`);
        onImagesChange([...currentUploaded, ...remainingTemp]);

        if (isVideo) {
          toast.success(`Video ${file.name} đã upload thành công`);
        }
      } catch (error: any) {
        console.error('[ImageUpload] Upload error:', error);
        toast.error(`Không thể tải ${validFiles[i].name}: ${error?.message || 'Lỗi upload'}`);
      }
    }

    // Final update - remove any failed uploads
    const finalImages = [...images, ...uploadedImages];

    console.log(`[ImageUpload] Final state:`, {
      total: finalImages.length,
      uploaded: uploadedImages.length,
      images: finalImages.map(img => ({
        type: img.type,
        url: img.url.substring(0, 50) + '...',
        isUploading: img.isUploading
      }))
    });

    onImagesChange(finalImages);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = async (index: number) => {
    const imageToRemove = images[index];
    const mediaType = imageToRemove.type === 'video' ? 'video' : 'ảnh';

    toast.info(`Đang xóa ${mediaType}...`);

    // Delete through blog-worker so the file is removed from the blog owner's Drive.
    if (!imageToRemove.id && (imageToRemove.driveFileId || imageToRemove.drive_file_id)) {
      try {
        await blogAPI.admin.deleteBlogMedia(blogId, imageToRemove.driveFileId || imageToRemove.drive_file_id!);
      } catch (error) {
        console.error('[ImageUpload] Failed to delete owner Drive file:', error);
        toast.error(`Không thể xóa ${mediaType} khỏi Drive của chủ blog.`);
        return;
      }
    }

    // Delete from backend database if image has been saved (has id and postId).
    if (imageToRemove.id && postId) {
      try {
        await blogAPI.admin.removePostImage(postId, imageToRemove.id);
        console.log(`[ImageUpload] Deleted from database: post ${postId}, image ${imageToRemove.id}`);
      } catch (error) {
        console.error('[ImageUpload] Failed to delete from database:', error);
        toast.error(`Không thể xóa ${mediaType} khỏi database. Vui lòng thử lại.`);
        return; // Don't remove from UI if backend deletion failed
      }
    }

    // Remove from UI/state.
    const newImages = images.filter((_, i) => i !== index);
    // Re-calculate sort_order
    const reorderedImages = newImages.map((img, i) => ({
      ...img,
      sort_order: i,
    }));
    onImagesChange(reorderedImages);

    if (imageToRemove.id && imageToRemove.id === coverImageId && onCoverChange) {
      const nextCover = reorderedImages.find((image) => image.id && image.type === 'image');
      onCoverChange(nextCover?.id ?? null);
    }

    toast.success(`Đã xóa ${mediaType} khỏi bài viết`);
  };

  const handleCaptionChange = (index: number, caption: string) => {
    const newImages = [...images];
    newImages[index] = { ...newImages[index], caption };
    onImagesChange(newImages);
  };

  const handleCaptionBlur = async (index: number) => {
    const image = images[index];
    if (!image.id || !postId || image.isUploading) return;
    try {
      await blogAPI.admin.updatePostImage(postId, image.id, { caption: image.caption || null });
    } catch (error) {
      console.error('[ImageUpload] Failed to update caption:', error);
      toast.error('Không thể lưu mô tả ảnh');
    }
  };

  const handleSetCover = async (image: PostImage) => {
    if (!postId || !image.id || image.type !== 'image' || image.id === coverImageId) return;
    try {
      await blogAPI.admin.updatePost(postId, { cover_image_id: image.id });
      onCoverChange?.(image.id);
      toast.success('Đã chọn ảnh bìa');
    } catch (error) {
      console.error('[ImageUpload] Failed to update cover:', error);
      toast.error('Không thể chọn ảnh bìa');
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === index) return;

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];

    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedImage);

    // Update sort_order
    const reorderedImages = newImages.map((img, i) => ({
      ...img,
      sort_order: i,
    }));

    onImagesChange(reorderedImages);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-2">
      {/* Upload Controls */}
      <div className="flex gap-2 items-center flex-wrap">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || (currentImageCount >= maxImages && currentVideoCount >= maxVideos)}
        >
          <ImagePlus className="h-4 w-4 mr-2" />
          Thêm ảnh/video
        </Button>

        <div className="flex gap-3 text-xs text-gray-500">
          <span>
            Ảnh: {currentImageCount}/{maxImages}
          </span>
          <span>•</span>
          <span>
            Video: {currentVideoCount}/{maxVideos}
          </span>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/mp4,video/webm"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Images Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {images.map((image, index) => (
            <div
              key={index}
              draggable={!disabled && !image.isUploading}
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={cn(
                'relative group bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200 transition-all',
                draggedIndex === index && 'opacity-50',
                !disabled && !image.isUploading && 'cursor-move hover:border-blue-400'
              )}
            >
              {/* Image or Video Thumbnail */}
              <div className="aspect-square relative">
                <img
                  src={image.type === 'video' && image.thumbnailUrl ? image.thumbnailUrl : image.url}
                  alt={image.caption || `${image.type === 'video' ? 'Video' : 'Image'} ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+TG9pPC90ZXh0Pjwvc3ZnPg==';
                  }}
                />

                {/* Video Icon Overlay */}
                {image.type === 'video' && !image.isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-black/60 rounded-full p-2">
                      <Video className="h-6 w-6 text-white" />
                    </div>
                  </div>
                )}

                {/* Drag Handle - Always visible on mobile, hover on desktop */}
                {!disabled && !image.isUploading && (
                  <div className="absolute top-1 left-1 bg-black/50 rounded p-1 shadow-md opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <GripVertical className="h-4 w-4 text-white" />
                  </div>
                )}

                {/* Remove Button - Always visible on mobile, hover on desktop */}
                {!disabled && !image.isUploading && (
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                    aria-label="Xóa"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}

                {image.type === 'image' && image.id && !image.isUploading && (
                  <button
                    type="button"
                    onClick={() => handleSetCover(image)}
                    disabled={disabled}
                    className={cn(
                      'absolute bottom-1 right-1 rounded px-1.5 py-0.5 text-[10px] font-medium shadow',
                      image.id === coverImageId
                        ? 'bg-blue-600 text-white'
                        : 'bg-white/90 text-gray-700 hover:bg-blue-50'
                    )}
                  >
                    {image.id === coverImageId ? 'Ảnh bìa' : 'Chọn bìa'}
                  </button>
                )}

                {/* Sort Order Badge */}
                <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                  {index + 1}
                </div>

                {/* Uploading Overlay */}
                {image.isUploading && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-white border-r-transparent" />
                  </div>
                )}
              </div>

              {/* Caption Input */}
              <div className="p-1.5 bg-white">
                <Input
                  placeholder="Mô tả..."
                  value={image.caption || ''}
                  onChange={(e) => handleCaptionChange(index, e.target.value)}
                  onBlur={() => handleCaptionBlur(index)}
                  disabled={disabled || image.isUploading}
                  className="text-xs h-6 px-2"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {images.length === 0 && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <ImagePlus className="h-10 w-10 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">
            Click "Thêm ảnh/video" để tải lên
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Giới hạn: {maxImages} ảnh • {maxVideos} video/bài viết
          </p>
          <p className="text-xs text-gray-500">
            Hỗ trợ: JPG, PNG, MP4, WebM (video tối đa 100MB)
          </p>
        </div>
      )}
    </div>
  );
};
